import type React from 'react';
import { forwardRef, type HTMLAttributes, useRef, useState } from 'react';
import { IconComponent } from '@/components/IconComponent';
import type { WithTestId } from '@/helpers/testIds';
import type { Validator } from '@/helpers/validators';
import useEventListener from '@/hooks/eventListener';
import type { RegistrationConfig } from '@/modules/registration/helpers/types';
import { useGetCommonStore } from '@/store';
import { InputStructure } from '../structures/InputStructure';
import { ScCheckbox, ScCheckboxBlock } from './styled';

export interface CheckboxBlockProps
  extends HTMLAttributes<HTMLInputElement>,
    WithTestId {
  id: string;
  label: React.ReactNode;
  checked?: boolean;
  disabled?: boolean;
  error?: React.ReactNode;
  tooltip?: string;
  validate?: Validator<any, any>;
  styleScheme?: RegistrationConfig['styleScheme'];
  isHidden?: boolean;
}

export const CheckboxBlock = forwardRef<HTMLInputElement, CheckboxBlockProps>(
  (
    { label, error, id, tooltip, testId, isHidden, styleScheme, ...rest },
    ref
  ) => {
    const useCommonStore = useGetCommonStore();

    const isMobile = useCommonStore.use.isMobileDevice();

    const tooltipRef = useRef<any>(null);

    const [isVisibleInfo, setIsVisibleInfo] = useState(false);

    useEventListener(
      'mouseenter',
      () => !isMobile && setIsVisibleInfo(true),
      tooltipRef
    );

    useEventListener(
      'mouseleave',
      () => !isMobile && setIsVisibleInfo(false),
      tooltipRef
    );

    useEventListener('click', (event) => {
      if (isMobile) {
        if (!tooltipRef.current?.contains(event.target) || isVisibleInfo) {
          setIsVisibleInfo(false);
        } else {
          setIsVisibleInfo(true);
        }
      }
    });

    return (
      <InputStructure error={error} isHidden={isHidden}>
        <ScCheckboxBlock
          $isVisibleInfo={isVisibleInfo}
          styleScheme={styleScheme}
        >
          <label htmlFor={id}>
            <ScCheckbox styleScheme={styleScheme}>
              <input
                {...rest}
                data-testid={testId}
                id={id}
                ref={ref}
                type="checkbox"
              />
              <span className="checkbox" />
            </ScCheckbox>
            <div className="label">{label}</div>
          </label>

          {tooltip && (
            <div className="tooltip" ref={tooltipRef}>
              <IconComponent className="tooltip-icon" name="tooltipIcon" />
              {isVisibleInfo && (
                <div className="tooltip-content">{tooltip}</div>
              )}
            </div>
          )}
        </ScCheckboxBlock>
      </InputStructure>
    );
  }
);
